<template>
  <div class="p-4">
    <div class="flex justify-between items-center mb-4">
      <div class="flex items-center gap-4">
        <!-- Кнопка "Создать заказ" -->
        <button
        v-can-write
        @click="openCreateOrderModal"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
          Создать заказ
        </button>
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="showTrashed" type="checkbox" class="rounded border-gray-300" @change="onShowTrashedChange" />
          <span class="text-sm text-gray-700">Показать отменённые</span>
        </label>
      </div>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full border-collapse bg-white shadow-md">
        <thead class="bg-gray-100">
          <tr>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">#</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Статус</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">План</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Факт</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Клиентский заказ</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Изделия</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Кол-во</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Готово</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="px-4 py-12 text-center">
              <div class="inline-block animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
            </td>
          </tr>
          <tr v-else-if="orders.length === 0">
            <td colspan="9" class="px-4 py-8 text-center text-gray-500">
              Нет данных
            </td>
          </tr>
          <tr
            v-for="order in orders"
            :key="order.id"
            :class="['border-b', showTrashed ? 'opacity-75 bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50']"
          >
            <td class="px-4 py-2 text-sm text-gray-700">{{ order.id }}</td>
            <td class="px-4 py-2">
              <div class="flex flex-col items-start gap-1">
                <span
                  :class="[
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
                    getStatusClasses(order.status)
                  ]"
                >
                  {{ getStatusName(order.status) }}
                </span>
                <!-- Отметка заказа с остановленными по несоответствию изделиями -->
                <span
                  v-if="stoppedTasksByOrder[order.id]"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-red-100 text-red-800"
                  :title="'Изделий остановлено по несоответствию: ' + stoppedTasksByOrder[order.id]"
                >
                  Есть несоответствие
                </span>
              </div>
            </td>
            <td class="px-4 py-2 text-sm text-gray-700 whitespace-nowrap tabular-nums">{{ formatShortDate(order.plan_start_datetime) || '—' }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 whitespace-nowrap tabular-nums">{{ formatDateRange(order.fact_start_datetime, order.fact_end_datetime) }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 break-words max-w-xs">{{ order.order?.number || '-' }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 break-words max-w-xs">
              <span :class="{ 'line-through text-gray-500': (order.order_product?.product || order.product)?.deleted_at }">
                {{ order.order_product?.product?.name || order.product?.name || 'Не указано' }}
              </span>
              <span v-if="(order.order_product?.product || order.product)?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
            </td>
            <td class="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">{{ order.count }}</td>
            <td class="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">{{ order.completed_task_count }} / {{ order.task_count }}</td>
            <td class="px-4 py-2">
              <div class="flex flex-wrap gap-1 justify-start">
                <template v-if="showTrashed">
                  <button
                    v-can-write
                    @click="confirmRestore(order.id)"
                    class="px-2 py-1 text-sm font-medium text-green-600 bg-green-100 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Восстановить
                  </button>
                </template>
                <template v-else>
                  <button
                    @click="pushToOrder(order.id)"
                    class="px-2 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Подробности
                  </button>
                  <button
                    v-if="order.status.trim().toLowerCase() === 'new'"
                    v-can-write
                    @click="startOrder(order.id)"
                    :disabled="startBlockReason(order) !== ''"
                    :title="startBlockReason(order)"
                    :class="[
                      'px-2 py-1 text-sm font-medium rounded-md focus:outline-none focus:ring-2',
                      startBlockReason(order) !== ''
                        ? 'text-orange-700 bg-orange-100 cursor-not-allowed'
                        : 'text-green-600 bg-green-100 hover:bg-green-200 focus:ring-green-500'
                    ]"
                  >
                    Запустить
                  </button>
                  <button
                    v-if="order.status.trim().toLowerCase() !== 'new'"
                    v-can-write
                    @click="cancelOrder(order.id)"
                    :disabled="order.status.trim().toLowerCase() !== 'work'"
                    :class="[
                      'px-2 py-1 text-sm font-medium rounded-md focus:outline-none focus:ring-2',
                      order.status.trim().toLowerCase() === 'work'
                        ? 'text-yellow-600 bg-yellow-100 hover:bg-yellow-200 focus:ring-yellow-500'
                        : 'text-gray-500 bg-gray-100 cursor-not-allowed opacity-60'
                    ]"
                  >
                    Снять с работы
                  </button>
                  <button
                    v-can-write
                    @click="confirmDelete(order.id)"
                    class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Заказ уходит из работы, но остаётся в отчётности — его видно по фильтру «Показать отменённые»"
                  >
                    Отменить производство
                  </button>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Pagination
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />

    <ConfirmModal
      v-if="showRestoreModal"
      :show="showRestoreModal"
      title="Восстановление заказа"
      message="Вы уверены, что хотите восстановить этот заказ на производство?"
      confirm-text="Восстановить"
      confirm-variant="success"
      @confirm="proceedRestore"
      @cancel="cancelRestore"
    />

    <!-- Запуск заблокирован: не хватает материалов либо стол не настроен (Z-21) -->
    <div v-if="isMissingModalVisible"
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-2xl p-6">
        <h2 class="text-lg font-bold mb-2">Запуск невозможен</h2>
        <p v-if="missingComponents.length || unlinkedDesks.length" class="text-sm text-gray-700 mb-4">
          Комплектование идёт строго по заданию: собрать неполный комплект комплектовщик не сможет,
          операция не закроется. Заказ запустится, когда всё перечисленное будет решено.
        </p>

        <!-- Текст отказа с бэкенда, когда запуск отбит уже при смене статуса -->
        <p v-if="startErrorMessage" class="text-sm text-gray-800 whitespace-pre-line mb-4">
          {{ startErrorMessage }}
        </p>

        <div v-if="missingComponents.length" class="max-h-72 overflow-y-auto border border-gray-200 rounded-md">
          <table class="w-full text-sm">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-3 py-2 text-left font-medium text-gray-700">Комплектующее</th>
                <th class="px-3 py-2 text-left font-medium text-gray-700">Артикул</th>
                <th class="px-3 py-2 text-right font-medium text-gray-700">Нужно</th>
                <th class="px-3 py-2 text-right font-medium text-gray-700">На складе</th>
                <th class="px-3 py-2 text-right font-medium text-gray-700">Не хватает</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in missingComponents" :key="row.product_id" class="border-t border-gray-100">
                <td class="px-3 py-2 text-gray-900">{{ row.product_name }}</td>
                <td class="px-3 py-2 text-gray-600">{{ row.sku || '—' }}</td>
                <td class="px-3 py-2 text-right text-gray-700">{{ row.required }} {{ row.unit_short_name }}</td>
                <td class="px-3 py-2 text-right text-gray-700">{{ row.available }} {{ row.unit_short_name }}</td>
                <td class="px-3 py-2 text-right font-semibold text-red-600">{{ row.missing }} {{ row.unit_short_name }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Столы, по которым комплектование вообще не с чего считать -->
        <div v-if="unlinkedDesks.length" :class="['max-h-52 overflow-y-auto', missingComponents.length ? 'mt-4' : '']">
          <ul class="space-y-2">
            <li
              v-for="desk in unlinkedDesks"
              :key="`${desk.operation_id}-${desk.reason}`"
              class="text-sm text-gray-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
            >
              {{ deskProblemText(desk) }}
            </li>
          </ul>
        </div>

        <div class="flex justify-end space-x-2 mt-4">
          <button
            @click="cancelMissingModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Понятно
          </button>
        </div>
      </div>
    </div>

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible"
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите отмену производства</h2>
        <p class="text-sm text-gray-700 mb-4">Отменить производство по этому заказу? Заказ уйдёт из работы, но останется в отчётности — его видно по фильтру «Показать отменённые», оттуда же его можно восстановить.</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Отменить производство
          </button>
        </div>
      </div>
    </div>

    <!-- Модальное окно создания заказа -->
    <AddProductOrderModal
      :is-visible="isCreateOrderModalVisible"
      @close="closeCreateOrderModal"
      @order-created="onOrderCreated"
    />

    <!-- Модальное окно документов для печати при запуске заказа -->
    <PrintFilesModal
      :is-visible="isPrintModalVisible"
      :files="printFiles"
      @confirm="confirmStartOrder"
      @cancel="cancelPrintModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import AddProductOrderModal from '@/components/production/AddProductOrderModal.vue';
import PrintFilesModal from '@/components/production/PrintFilesModal.vue';
import ConfirmModal from '@/components/common/ConfirmModal.vue';
import EmptyState from "@/components/EmptyState.vue";
import Pagination from '@/components/Pagination.vue';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: {
    EmptyState,
    AddProductOrderModal,
    PrintFilesModal,
    ConfirmModal,
    Pagination,
  },
  data() {
    return {
      orders: [], // Список заказов
      loading: true, // Флаг загрузки
      isCreateOrderModalVisible: false, // Флаг видимости модального окна создания
      isConfirmModalVisible: false,
      isPrintModalVisible: false,
      printFiles: [],
      // Причины, по которым запуск заказа невозможен (Z-21)
      isMissingModalVisible: false,
      missingComponents: [],
      unlinkedDesks: [],
      startErrorMessage: '',
      orderIdToStart: null,
      orderIdToDelete: null,
      showRestoreModal: false,
      orderIdToRestore: null,
      showTrashed: false,
      pagination: {
        currentPage: 1,
        perPage: 15,
        total: 0,
        lastPage: 1
      }
    };
  },
  computed: {
    // Сколько изделий заказа остановлено по несоответствию — считаем один раз на выборку,
    // отдельного поля под это бэкенд не отдаёт
    stoppedTasksByOrder() {
      const map = {};
      this.orders.forEach(order => {
        map[order.id] = (order.tasks || []).filter(task => task.stopped_at).length;
      });
      return map;
    },
  },
  created() {
    createUrlStateManager(this, {
      'pagination.currentPage': { type: 'number', default: 1, urlKey: 'page' },
      showTrashed: { type: 'boolean', default: false, urlKey: 'trashed' },
    }, {
      routeName: 'Production',
      additionalQuery: { tab: 'orders' }
    });

    this.fetchOrders(this.pagination.currentPage);
  },
  methods: {
    async fetchOrders(page = 1) {
      this.loading = true;
      try {
        const { data } = await axios.post('/api/productionOrder/getProductionOrders', {
          page: page,
          per_page: this.pagination.perPage,
          only_trashed: this.showTrashed
        });

        if (data.success) {
          this.orders = data.data.data;
          this.pagination.currentPage = data.data.current_page;
          this.pagination.total = data.data.total;
          this.pagination.lastPage = data.data.last_page;
        } else {
          console.error('Ошибка при получении заказов:', data);
        }
      } catch (error) {
        console.error('Ошибка при загрузке заказов:', error);
      } finally {
        this.loading = false;
      }
    },

    onShowTrashedChange() {
      this.orders = [];
      this.pagination.currentPage = 1;
      const query = { ...this.$route.query };
      delete query.page;
      query.trashed = this.showTrashed;
      this.$router.replace({ name: this.$route.name, params: this.$route.params, query }).catch(() => {});
      this.fetchOrders(1);
    },
    changePage(page) {
      this.fetchOrders(page);
    },
    /**
     * После создания заказа модалка эмитит созданный ID; чтобы он не попал
     * первым аргументом в fetchOrders(page=1) и не «улетал» в page=ID,
     * сбрасываем на первую страницу — там виден свежесозданный заказ.
     */
    onOrderCreated() {
      this.fetchOrders(1);
    },
    openCreateOrderModal() {
      this.isCreateOrderModalVisible = true;
    },
    closeCreateOrderModal() {
      this.isCreateOrderModalVisible = false;
    },
    pushToOrder(id) {
      this.$router.push({
        name: 'ProductionOrderShow',
        params: { id },
        query: { ...this.$route.query, moduleId: this.$route.params.id }
      });
    },
    getStatusName(status) {
      const statusMap = {
        new: 'Новый',
        work: 'В работе',
        done: 'Завершен',
      };
      return statusMap[status] || status;
    },

    getStatusClasses(status) {
      const statusClasses = {
        'new': 'bg-yellow-100 text-yellow-800',
        'work': 'bg-blue-100 text-blue-800',
        'done': 'bg-green-100 text-green-800',
      };
      return statusClasses[status] || 'bg-gray-100 text-gray-800';
    },

    /**
     * Короткая дата вида «25.08». Пустое или некорректное значение — null,
     * чтобы вызывающий код сам решил, чем заполнить ячейку.
     */
    formatShortDate(dateString) {
      if (!dateString) return null;

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');

      return `${day}.${month}`;
    },

    /**
     * Диапазон дат одной ячейкой: «25.08-26.08».
     * Если даты совпадают или известна только одна из них — выводим одну дату,
     * если дат нет вовсе — прочерк.
     */
    formatDateRange(startDate, endDate) {
      const start = this.formatShortDate(startDate);
      const end = this.formatShortDate(endDate);

      if (!start && !end) return '—';
      if (!start || !end || start === end) return start || end;

      return `${start}-${end}`;
    },
    async startOrder(id) {
      this.startErrorMessage = '';
      try {
        // Нехватка материалов и стол без склада комплектования запуск блокируют:
        // мастер видит причины до старта, а не когда комплектовщик встал у стола (Z-21)
        const { missing, unlinkedDesks } = await this.loadMissingComponents(id);
        if (missing.length > 0 || unlinkedDesks.length > 0) {
          this.missingComponents = missing;
          this.unlinkedDesks = unlinkedDesks;
          this.orderIdToStart = id;
          this.isMissingModalVisible = true;
          return;
        }

        await this.proceedStartOrder(id);
      } catch (error) {
        this.showStartError(error);
      }
    },

    /**
     * Причины блокировки запуска: нехватка на складе и столы без склада
     * комплектования. Оба списка пустые — запуск пройдёт.
     */
    async loadMissingComponents(id) {
      const { data } = await axios.post('/api/productionOrder/getMissingComponents', { id });

      return {
        missing: Array.isArray(data?.data?.missing) ? data.data.missing : [],
        unlinkedDesks: Array.isArray(data?.data?.unlinked_desks) ? data.data.unlinked_desks : [],
      };
    },

    /** Что именно не настроено у стола и где это поправить */
    deskProblemText(desk) {
      if (desk.reason === 'no_module') {
        return `У операции «${desk.operation_name}» не указан рабочий стол`;
      }

      const deskName = desk.module_name || 'без названия';

      return `Для стола «${deskName}» не указан склад комплектования `
        + '(карточка склада → Структура → Настройки → привязка к столам, значение «Склад комплектования»)';
    },

    // Почему заказ нельзя запустить: серийная продукция или ключи ПО не указаны.
    // Пустая строка — кнопка «Запустить» активна
    startBlockReason(order) {
      if (order.serial_components_ready === false) {
        return 'Укажите требуемую серийную продукцию по всем изделиям заказа';
      }
      if (order.software_keys_ready === false) {
        return 'Укажите ключи активации ПО по всем изделиям заказа';
      }
      return '';
    },

    cancelMissingModal() {
      this.isMissingModalVisible = false;
      this.missingComponents = [];
      this.unlinkedDesks = [];
      this.startErrorMessage = '';
      this.orderIdToStart = null;
    },

    /**
     * Отказ в запуске приходит с бэкенда готовым текстом — показываем его как есть.
     * Тоста нет (компонент открыт вне общего layout) — тот же текст уходит в окно,
     * иначе пользователь увидел бы, что «ничего не произошло».
     */
    showStartError(error) {
      console.error('Ошибка при запуске заказа:', error);
      const message = error.response?.data?.message || 'Не удалось запустить заказ. Попробуйте ещё раз.';

      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(message);

        return;
      }

      this.startErrorMessage = message;
      this.isMissingModalVisible = true;
    },

    async proceedStartOrder(id) {
      try {
        const { data } = await axios.post('/api/productionOrder/getPrintFiles', { id });
        if (data.files && data.files.length > 0) {
          this.printFiles = data.files;
          this.orderIdToStart = id;
          this.isPrintModalVisible = true;
        } else {
          await this.confirmStartOrder(id);
        }
      } catch (error) {
        this.showStartError(error);
      }
    },

    async confirmStartOrder(id) {
      const orderId = id ?? this.orderIdToStart;
      if (!orderId) return;
      try {
        await axios.post('/api/productionOrder/changeStatus', { id: orderId, status: 'work' });
        this.cancelPrintModal();
        await this.fetchOrders(this.pagination.currentPage);
      } catch (error) {
        // Отказ в запуске (422) закрывает окно печати: иначе текст причины
        // оказался бы под ним
        this.cancelPrintModal();
        this.showStartError(error);
      }
    },

    cancelPrintModal() {
      this.isPrintModalVisible = false;
      this.printFiles = [];
      this.orderIdToStart = null;
    },
    
    async cancelOrder(id) {
      try {
        await axios.post('/api/productionOrder/changeStatus', {id: id, status: 'new'});
        await this.fetchOrders(this.pagination.currentPage);
      } catch (error) {
        console.error('Ошибка при отмене заказа:', error);
      }
    },
    
    confirmDelete(id) {
      this.orderIdToDelete = id; // Сохраняем ID заказа
      this.isConfirmModalVisible = true; // Показываем модальное окно
    },

    cancelDelete() {
      this.isConfirmModalVisible = false; // Скрываем модальное окно
      this.orderIdToDelete = null; // Очищаем ID
    },

    confirmRestore(id) {
      this.orderIdToRestore = id;
      this.showRestoreModal = true;
    },
    cancelRestore() {
      this.showRestoreModal = false;
      this.orderIdToRestore = null;
    },
    async proceedRestore() {
      if (!this.orderIdToRestore) return;
      try {
        await axios.post('/api/productionOrder/restoreProductionOrder', { id: this.orderIdToRestore });
        await this.fetchOrders(this.pagination.currentPage);
      } catch (error) {
        console.error('Ошибка при восстановлении заказа:', error);
      } finally {
        this.cancelRestore();
      }
    },

    async proceedDelete() {
      if (!this.orderIdToDelete) {
        console.error('ID заказа не указан');
        return;
      }

      try {
        await axios.post('/api/productionOrder/deleteProductionOrder', {id: this.orderIdToDelete});
        await this.fetchOrders(this.pagination.currentPage);
        this.cancelDelete(); // Скрываем модальное окно
      } catch (error) {
        console.error('Ошибка при удалении заказа:', error);
      }
    },
  },
};
</script>
